import React, { useEffect, useState } from "react";
import { ListOrdered, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Card, Pill, PrimaryButton } from "./UI";
import { ChoiceSelect } from "./ChoiceSelect";
import { DAY_LABELS, TIME_PREFS } from "./portal/WaitlistOffers";
import api from "../lib/api";

const prefLabel = (v) => TIME_PREFS.find((t) => t.value === v)?.label || v;

export const WaitlistPanel = ({ clients }) => {
  const [data, setData] = useState({ entries: [], offers: [] });
  const [form, setForm] = useState({ client_id: "", days: [1, 2, 3, 4, 5], time_pref: "any" });
  const load = () => api.get("/waitlist").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post("/waitlist", form); toast.success("Added to waitlist"); setForm({ ...form, client_id: "" }); load(); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't add"); }
  };
  const remove = async (id) => { await api.delete(`/waitlist/${id}`); load(); };
  const toggle = (d) => setForm({ ...form, days: form.days.includes(d) ? form.days.filter((x) => x !== d) : [...form.days, d] });

  return (
    <Card className="mt-5" testid="waitlist-panel">
      <h3 className="font-serif text-xl mb-1 flex items-center gap-2"><ListOrdered className="w-5 h-5" style={{ color: "var(--mf-accent)" }} /> Waitlist auto-fill</h3>
      <p className="text-xs mb-4" style={{ color: "var(--mf-text-muted)" }}>When a session is cancelled 3+ hours ahead, every matching waitlisted client with an active portal is emailed — first to claim gets it.</p>
      <div className="grid md:grid-cols-[minmax(0,1fr)_auto] gap-3 items-start mb-4">
        <div className="space-y-2">
          <ChoiceSelect value={form.client_id || "none"} onChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })} testid="waitlist-client"
            options={[{ value: "none", label: "Choose client…" }, ...clients.filter((c) => c.status !== "archived").map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name}` }))]} />
          <div className="flex flex-wrap gap-1.5">
            {DAY_LABELS.map((d, i) => (
              <button key={d} onClick={() => toggle(i)} className="w-10 py-1 rounded-full text-xs" style={{ background: form.days.includes(i) ? "var(--mf-primary)" : "var(--mf-muted)", color: form.days.includes(i) ? "#FAFAF8" : "var(--mf-text)" }} data-testid={`waitlist-add-day-${i}`}>{d}</button>
            ))}
          </div>
          <ChoiceSelect value={form.time_pref} onChange={(v) => setForm({ ...form, time_pref: v })} options={TIME_PREFS} testid="waitlist-add-time" />
        </div>
        <PrimaryButton onClick={add} disabled={!form.client_id || !form.days.length} testid="waitlist-add-btn"><span className="flex items-center gap-1"><Plus className="w-4 h-4" /> Add</span></PrimaryButton>
      </div>
      <div className="space-y-2">
        {data.entries.length === 0 && <div className="text-sm" style={{ color: "var(--mf-text-muted)" }}>No one on the waitlist yet.</div>}
        {data.entries.map((e) => (
          <div key={e.id} className="p-3 rounded-xl flex flex-wrap items-center gap-2" style={{ background: "var(--mf-muted)" }} data-testid={`waitlist-entry-${e.id}`}>
            <span className="text-sm font-medium flex-1">{e.name}</span>
            <span className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{e.days.map((d) => DAY_LABELS[d]).join(", ")} · {prefLabel(e.time_pref)}</span>
            {!e.portal_active && <Pill variant="warn">No portal — can't be offered</Pill>}
            <Pill>{e.added_by === "client" ? "Self-added" : "Added by you"}</Pill>
            <button onClick={() => remove(e.id)} className="p-1.5 rounded-lg hover:bg-black/5" aria-label="Remove" data-testid={`waitlist-remove-${e.id}`}><Trash2 className="w-4 h-4" style={{ color: "var(--mf-text-muted)" }} /></button>
          </div>
        ))}
      </div>
      {data.offers.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-medium mb-1" style={{ color: "var(--mf-text-muted)" }}>Recent offers</div>
          {data.offers.map((o) => (
            <div key={o.id} className="text-xs py-1 flex gap-2" data-testid={`offer-row-${o.id}`}>
              <span>{new Date(o.start_time).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</span>
              <span>· offered to {o.offered_to.length}</span>
              <b style={{ color: o.status === "claimed" ? "#4a7a4e" : "var(--mf-text-muted)" }}>{o.status}</b>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
